import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LaunchIcon from '@mui/icons-material/Launch';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import { adminService, type CreateTenantPayload, type UpdateTenantPayload } from '../../services/admin';
import {
  TIER_LIMITS,
  type BillingMode,
  type BillingStatus,
  type FeatureFlags,
  type QuotaOverrides,
  type Tenant,
  type Tier,
  type User,
} from '@traaker/shared';

const TIERS: Tier[] = ['lite', 'pro', 'enterprise'];
const BILLING_MODES: BillingMode[] = ['manual', 'stripe'];
const BILLING_STATUSES: BillingStatus[] = ['pending', 'active', 'past_due', 'suspended', 'cancelled'];
const QUOTA_KEYS: Array<keyof QuotaOverrides> = [
  'brands',
  'zones',
  'users',
  'competitors',
  'scansPerMonth',
  'pagesPerAudit',
  'apiCallsPerDay',
  'concurrentAgents',
  'socialIntegrations',
  'brandNarrativeQueries',
  'brandAuthorityQueries',
  'brandReferralQueriesPerLevel',
  'brandNarrativeRunsPerMonth',
  'brandAuthorityRunsPerMonth',
  'brandReferralRunsPerMonth',
];

export default function AdminTenantForm() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const isEdit = !!tenantId;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [users, setUsers] = useState<User[]>([]);

  // Form state
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [tier, setTier] = useState<Tier>('pro');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');

  const [quotaOverrides, setQuotaOverrides] = useState<Record<string, string>>({});

  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({
    newsWatchEnabled: false,
    competitorsEnabled: false,
    tasksEnabled: false,
    reportsEnabled: false,
  });

  const [billingMode, setBillingMode] = useState<BillingMode>('manual');
  const [billingStatus, setBillingStatus] = useState<BillingStatus>('pending');
  const [contractAmountEuros, setContractAmountEuros] = useState<string>('');
  const [contractInterval, setContractInterval] = useState<'month' | 'year'>('month');
  const [contractNotes, setContractNotes] = useState('');

  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [generatingCheckout, setGeneratingCheckout] = useState(false);
  const [resendingWelcome, setResendingWelcome] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDeleteTenant = async () => {
    if (!tenantId) return;
    setDeleting(true);
    setError(null);
    try {
      await adminService.deleteTenant(tenantId, deleteConfirmation);
      navigate('/admin/tenants');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
      setDeleting(false);
    }
  };

  const handleResendWelcome = async () => {
    if (!tenantId) return;
    setResendingWelcome(true);
    setError(null);
    try {
      const { to } = await adminService.resendWelcomeEmail(tenantId);
      setMessage(`Email de bienvenue renvoyé à ${to}`);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\u2019envoi');
    } finally {
      setResendingWelcome(false);
    }
  };

  const handleGenerateCheckout = async () => {
    if (!tenantId) return;
    setGeneratingCheckout(true);
    setError(null);
    try {
      const origin = window.location.origin;
      const { url } = await adminService.createCheckoutSession(tenantId, {
        successUrl: `${origin}/admin/tenants/${tenantId}?stripe=success`,
        cancelUrl: `${origin}/admin/tenants/${tenantId}?stripe=cancelled`,
      });
      setCheckoutUrl(url);
    } catch (err: any) {
      setError(err.message || 'Erreur génération lien Stripe');
    } finally {
      setGeneratingCheckout(false);
    }
  };

  useEffect(() => {
    if (!isEdit || !tenantId) return;
    adminService
      .getTenant(tenantId)
      .then(({ tenant: t, users: u }) => {
        setTenant(t);
        setUsers(u);
        setName(t.name);
        setDomain(t.domain);
        setTier(t.tier);
        setBillingMode(t.billingMode);
        setBillingStatus(t.billingStatus);
        setContractAmountEuros(
          t.contractAmountCents != null ? String(t.contractAmountCents / 100) : '',
        );
        setContractInterval(t.contractInterval || 'month');
        setContractNotes(t.contractNotes || '');
        setFeatureFlags({
          newsWatchEnabled: !!t.settings.featureFlags?.newsWatchEnabled,
          competitorsEnabled: !!t.settings.featureFlags?.competitorsEnabled,
          tasksEnabled: !!t.settings.featureFlags?.tasksEnabled,
          reportsEnabled: !!t.settings.featureFlags?.reportsEnabled,
        });
        const q: Record<string, string> = {};
        const qo = t.settings.quotaOverrides || {};
        for (const k of QUOTA_KEYS) {
          const v = (qo as any)[k];
          if (typeof v === 'number') q[k] = String(v);
        }
        setQuotaOverrides(q);
      })
      .catch((err) => setError(err.message || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [isEdit, tenantId]);

  const tierDefaults = useMemo(() => TIER_LIMITS[tier], [tier]);

  const buildSettings = () => {
    const qo: QuotaOverrides = {};
    for (const k of QUOTA_KEYS) {
      const raw = quotaOverrides[k];
      if (raw != null && raw !== '') {
        const n = Number(raw);
        if (!Number.isNaN(n)) (qo as any)[k] = n;
      }
    }
    return {
      quotaOverrides: Object.keys(qo).length ? qo : undefined,
      featureFlags,
    };
  };

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: CreateTenantPayload = {
        name,
        domain,
        tier,
        ownerEmail,
        ownerName,
        settings: buildSettings(),
        billing: {
          mode: billingMode,
          amountCents: contractAmountEuros ? Math.round(Number(contractAmountEuros) * 100) : undefined,
          interval: contractInterval,
          notes: contractNotes || undefined,
        },
      };
      const { tenant: created } = await adminService.createTenant(payload);
      navigate(`/admin/tenants/${created.tenantId}`);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!tenantId) return;
    setSaving(true);
    setError(null);
    try {
      const payload: UpdateTenantPayload = {
        name,
        domain,
        tier,
        settings: buildSettings(),
        billingMode,
        contractAmountCents: contractAmountEuros ? Math.round(Number(contractAmountEuros) * 100) : undefined,
        contractInterval,
        contractNotes: contractNotes || undefined,
      };
      await adminService.updateTenant(tenantId, payload);
      if (tenant && tenant.billingStatus !== billingStatus) {
        await adminService.setBillingStatus(tenantId, billingStatus);
      }
      setMessage('Tenant mis à jour');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/tenants')} size="small">
          Retour
        </Button>
        <Typography variant="h5" fontWeight={700}>
          {isEdit ? `Tenant — ${name || tenantId}` : 'Nouveau tenant'}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}

      <Stack spacing={3}>
        {/* Basics */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Informations</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField label="Nom" value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField label="Domaine" value={domain} onChange={(e) => setDomain(e.target.value)} fullWidth size="small" />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tier</InputLabel>
                  <Select label="Tier" value={tier} onChange={(e) => setTier(e.target.value as Tier)}>
                    {TIERS.map((t) => (
                      <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {!isEdit && (
                <>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField label="Email propriétaire" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} fullWidth size="small" />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField label="Nom propriétaire" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} fullWidth size="small" />
                  </Grid>
                </>
              )}
            </Grid>
            {!isEdit && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Le propriétaire est créé en statut <strong>pending</strong>. Il devra créer son compte Cognito avec cette adresse email pour activer son accès.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Feature flags */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Fonctionnalités</Typography>
            <Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!featureFlags.tasksEnabled}
                    onChange={(e) => setFeatureFlags({ ...featureFlags, tasksEnabled: e.target.checked })}
                  />
                }
                label="Tâches (Tasks)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!featureFlags.newsWatchEnabled}
                    onChange={(e) => setFeatureFlags({ ...featureFlags, newsWatchEnabled: e.target.checked })}
                  />
                }
                label="Veille news (News Watch)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!featureFlags.competitorsEnabled}
                    onChange={(e) => setFeatureFlags({ ...featureFlags, competitorsEnabled: e.target.checked })}
                  />
                }
                label="Concurrents"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!featureFlags.reportsEnabled}
                    onChange={(e) => setFeatureFlags({ ...featureFlags, reportsEnabled: e.target.checked })}
                  />
                }
                label="Rapports (white-label agence)"
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Billing */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Facturation</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Mode</InputLabel>
                  <Select label="Mode" value={billingMode} onChange={(e) => setBillingMode(e.target.value as BillingMode)}>
                    {BILLING_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              {isEdit && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Statut</InputLabel>
                    <Select label="Statut" value={billingStatus} onChange={(e) => setBillingStatus(e.target.value as BillingStatus)}>
                      {BILLING_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Montant négocié (€)"
                  type="number"
                  value={contractAmountEuros}
                  onChange={(e) => setContractAmountEuros(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Intervalle</InputLabel>
                  <Select
                    label="Intervalle"
                    value={contractInterval}
                    onChange={(e) => setContractInterval(e.target.value as 'month' | 'year')}
                  >
                    <MenuItem value="month">Mensuel</MenuItem>
                    <MenuItem value="year">Annuel</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Notes contrat"
                  value={contractNotes}
                  onChange={(e) => setContractNotes(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Stripe checkout link (edit mode + stripe mode) */}
        {isEdit && billingMode === 'stripe' && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Lien de paiement Stripe</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Génère un lien Checkout à envoyer au client (SEPA + carte). Le statut du compte sera
                mis à jour automatiquement par Stripe après paiement.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: checkoutUrl ? 2 : 0 }}>
                <Button
                  variant="contained"
                  onClick={handleGenerateCheckout}
                  disabled={generatingCheckout || !contractAmountEuros}
                >
                  {generatingCheckout ? 'Génération…' : 'Générer un lien de paiement'}
                </Button>
                {tenant?.stripeCustomerId && (
                  <Typography variant="caption" color="text.secondary">
                    Stripe Customer: {tenant.stripeCustomerId}
                  </Typography>
                )}
              </Stack>
              {checkoutUrl && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      value={checkoutUrl}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      sx={{ minWidth: 400 }}
                    />
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => navigator.clipboard.writeText(checkoutUrl)}
                    >
                      Copier
                    </Button>
                    <Button
                      size="small"
                      startIcon={<LaunchIcon />}
                      component="a"
                      href={checkoutUrl}
                      target="_blank"
                      rel="noopener"
                    >
                      Ouvrir
                    </Button>
                  </Stack>
                </Alert>
              )}
              {!contractAmountEuros && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Renseigne le montant négocié avant de générer le lien.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quota overrides */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Quotas (overrides)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Laisse vide pour utiliser la valeur par défaut du tier <strong>{tier}</strong>. -1 = illimité.
            </Typography>
            <Grid container spacing={2}>
              {QUOTA_KEYS.map((k) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={k}>
                  <TextField
                    label={k}
                    type="number"
                    value={quotaOverrides[k] || ''}
                    onChange={(e) => setQuotaOverrides({ ...quotaOverrides, [k]: e.target.value })}
                    placeholder={String((tierDefaults as any)?.[k] ?? '')}
                    fullWidth
                    size="small"
                    helperText={`défaut: ${(tierDefaults as any)?.[k] ?? '—'}`}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {isEdit && users.length > 0 && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>Utilisateurs ({users.length})</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={handleResendWelcome}
                  disabled={resendingWelcome}
                >
                  {resendingWelcome ? 'Envoi…' : 'Renvoyer l\u2019email de bienvenue'}
                </Button>
              </Box>
              <Stack spacing={1}>
                {users.map((u) => (
                  <Box key={u.userId} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {u.role}{u.cognitoSub.startsWith('pending:') ? ' · pending' : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        <Divider />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEdit && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                setDeleteConfirmation('');
                setDeleteDialogOpen(true);
              }}
            >
              Supprimer le tenant
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => navigate('/admin/tenants')}>Annuler</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving || (!isEdit && (!name || !ownerEmail || !ownerName))}
            onClick={isEdit ? handleUpdate : handleCreate}
          >
            {isEdit ? 'Enregistrer' : 'Créer'}
          </Button>
        </Box>
      </Stack>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => (deleting ? undefined : setDeleteDialogOpen(false))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Supprimer le tenant</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Cette action est <strong>irréversible</strong>. Toutes les données du tenant
            <strong> {name}</strong> (utilisateurs, marques, audits, etc.) seront supprimées
            définitivement.
            <br />
            <br />
            Pour confirmer, tape le nom du tenant exactement : <strong>{name}</strong>
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Nom du tenant"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            disabled={deleting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteTenant}
            disabled={
              deleting ||
              deleteConfirmation.trim().toLowerCase() !== name.trim().toLowerCase()
            }
          >
            {deleting ? 'Suppression…' : 'Supprimer définitivement'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
